import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { isStepUpCode } from '@/lib/step-up';

/** Texto fixo de ajuda: o código do login acabou de ser usado e não vale de novo (TOTP de uso único). */
export const STEP_UP_HELP =
  'Use o código atual do autenticador. Se acabou de entrar, aguarde o próximo código (ele muda a cada 30 s).';

/** O que recebe foco pelo Tab dentro do modal. */
const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface StepUpDialogProps {
  title: string;
  /** As consequências da ação, lidas antes de digitar o código. */
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  /** Mensagem da última recusa (código errado ou já usado); o campo continua editável. */
  error?: string | null;
  onSubmit: (code: string) => void | Promise<void>;
  onCancel: () => void;
  /**
   * "Usar passkey" (C14, tarefa 14.6): presente só quando o navegador tem WebAuthn e a conta tem
   * passkey ativa (`useStepUpDialog().passkeyAvailable`). O código continua como alternativa.
   */
  onPasskey?: () => void | Promise<void>;
  /**
   * Só passkey (change platform-mcp-writes, W-D9): escrita direta e controle total não aceitam o
   * código do autenticador. Sem `onPasskey` (navegador sem WebAuthn ou conta sem passkey), o modal
   * explica o que falta e só permite cancelar.
   */
  passkeyOnly?: boolean;
}

/**
 * Confirmação de ação sensível com o código TOTP atual do operador (step-up, change
 * platform-backoffice-hardening, C6). Mesmo padrão visual do `SimpleConfirmDialog`, com um campo
 * de 6 dígitos igual ao do `MfaVerifyPage`. Só confirma com 6 dígitos: recovery code não cabe no
 * campo nem vale de step-up na API.
 *
 * Quem usa: desativar tenant, cancelar assinatura, aplicar preço às assinaturas de verdade,
 * reembolsar pacote de atendimentos (`TenantAiUnitsSection`), aprovar a concessão em
 * `/mcp/autorizar` e cadastrar passkey (este, só com o código). Com `onPasskey`, oferece também
 * a confirmação por passkey, que vale onde o código vale.
 */
export function StepUpDialog({
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  busy = false,
  error = null,
  onSubmit,
  onCancel,
  onPasskey,
  passkeyOnly = false,
}: StepUpDialogProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const helpId = `${id}-help`;
  const inputId = `${id}-code`;
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState('');

  const valid = isStepUpCode(code);

  // Foco no campo ao abrir e de novo a cada recusa, com o código selecionado para redigitar.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [error]);

  // O foco não sai do modal (`aria-modal` não prende nada sozinho): o Tab e o Shift+Tab dão a
  // volta dentro dele, e o foco que cai fora (um link da página, o formulário de trás) volta para
  // o primeiro controle habilitado do modal. Sem isso, o operador editaria o formulário que fica
  // atrás do overlay com o modal aberto.
  useEffect(() => {
    function focusables(): HTMLElement[] {
      const dialog = dialogRef.current;
      if (!dialog) return [];
      return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !(el as HTMLButtonElement).disabled,
      );
    }

    function onKeyDown(e: KeyboardEvent) {
      const dialog = dialogRef.current;
      if (e.key !== 'Tab' || !dialog) return;
      const items = focusables();
      const active = document.activeElement;
      const inside = active instanceof Node && dialog.contains(active);
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (!inside || active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    }

    function onFocusIn(e: FocusEvent) {
      const dialog = dialogRef.current;
      if (!dialog || !(e.target instanceof Node) || dialog.contains(e.target)) return;
      (focusables()[0] ?? dialog).focus();
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    void onSubmit(code);
  }

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !busy) onCancel();
        }}
        style={{
          background: 'var(--color-surface)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 440,
          width: '90%',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h2 id={titleId} style={{ marginTop: 0, fontSize: 18 }}>
          {title}
        </h2>
        <div id={descriptionId} style={{ color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          {description}
        </div>

        {passkeyOnly && (
          <div>
            {onPasskey ? (
              <button
                type="button"
                className="btn"
                onClick={() => void onPasskey()}
                disabled={busy}
                style={{ width: '100%' }}
              >
                Usar passkey
              </button>
            ) : (
              <div className="alert alert-error" role="alert">
                Esta confirmação pede uma passkey, e o código do autenticador não vale aqui. Cadastre uma
                passkey em Minha conta, num navegador com suporte, e tente de novo.
              </div>
            )}
            {error && (
              <div className="alert alert-error" role="alert" style={{ marginTop: 8 }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
                {cancelLabel}
              </button>
            </div>
          </div>
        )}

        {!passkeyOnly && onPasskey && (
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              className="btn"
              onClick={() => void onPasskey()}
              disabled={busy}
              style={{ width: '100%' }}
            >
              Usar passkey
            </button>
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 13, textAlign: 'center' }}>
              ou digite o código do autenticador
            </p>
          </div>
        )}

        {!passkeyOnly && (
        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor={inputId}>Código do autenticador</label>
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              aria-describedby={helpId}
              aria-invalid={error ? true : undefined}
              disabled={busy}
              required
            />
            <p id={helpId} className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
              {STEP_UP_HELP}
            </p>
          </div>

          {error && (
            <div className="alert alert-error" role="alert" style={{ marginTop: 8 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </button>
            <button
              type="submit"
              className="btn btn-danger"
              disabled={!valid || busy}
              style={{ padding: '8px 16px', borderRadius: 8 }}
            >
              {busy ? `${confirmLabel}…` : confirmLabel}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
